import type {
  SurfaceCoreSystemEntitiesSaasTenantSaasTenantOutput,
  UserSession,
} from '@/api/client';

import { useAuth } from './use-auth';
import { useTenantId } from './use-tenant-id';

/**
 * Tenant-scoped feature keys. Add new values here when rolling out features by tenant.
 * Map to the actual key on the /me saas_tenants entry in TENANT_FEATURE_TO_KEY.
 */
export const TENANT_FEATURES = {
  LEASE_AUDIT: 'lease_audit',
} as const;

export type TenantFeature =
  (typeof TENANT_FEATURES)[keyof typeof TENANT_FEATURES];

const TENANT_FEATURE_TO_KEY: Record<
  TenantFeature,
  keyof SurfaceCoreSystemEntitiesSaasTenantSaasTenantOutput
> = {
  [TENANT_FEATURES.LEASE_AUDIT]: 'enable_lease_audit',
};

function getSelectedTenant(
  user: UserSession | null | undefined,
  tenantId: string | null | undefined
): SurfaceCoreSystemEntitiesSaasTenantSaasTenantOutput | undefined {
  const activeTenantId = tenantId ?? user?.saas_tenant_id;
  const tenants = user?.saas_tenants;
  if (!activeTenantId || !tenants?.length) return undefined;
  return tenants.find((t) => t.id === activeTenantId);
}

/**
 * Pure helper: whether the given tenant feature is enabled for the current user's selected tenant.
 * Returns true only when the tenant exists and the flag is explicitly true; otherwise false.
 */
export function getTenantFeatureEnabled(
  user: UserSession | null | undefined,
  feature: TenantFeature,
  tenantId?: string | null
): boolean {
  const tenant = getSelectedTenant(user, tenantId);
  if (!tenant) return false;
  const key = TENANT_FEATURE_TO_KEY[feature];
  const value = tenant[key];
  return value === true;
}

/**
 * Hook: whether the given tenant feature is enabled for the current user's selected tenant.
 */
export function useTenantFeature(feature: TenantFeature): boolean {
  const { user } = useAuth();
  const tenantId = useTenantId();
  return getTenantFeatureEnabled(user, feature, tenantId);
}

/**
 * Hook: whether each of the given tenant features is enabled for the current user's selected tenant.
 */
export function useTenantFeatures(
  features: TenantFeature[]
): Record<TenantFeature, boolean> {
  const { user } = useAuth();
  const tenantId = useTenantId();
  const result = {} as Record<TenantFeature, boolean>;
  for (const feature of features) {
    result[feature] = getTenantFeatureEnabled(user, feature, tenantId);
  }
  return result;
}
